import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Anchor, Ship, DollarSign, Users, AlertTriangle, Calendar, TrendingUp, BarChart3, Filter } from 'lucide-react';
import { Card } from "../ui/card";
import { api } from '../../lib/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

import LineChartCard from "./LineChartCard";
import SummaryCard from "./SummaryCard";
import BarChartCard from "./BarChartCard";
import DashboardActions from "./DashboardActions";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const describeSelectedMonth = (value) => {
  if (!value) return '';
  const [yearStr, monthStr] = value.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  if (Number.isNaN(year) || Number.isNaN(month)) return '';
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
};

const parseDateInputValue = (value) => {
  if (!value) return null;
  const [yearStr, monthStr, dayStr] = value.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

// ---- Nuevas utilidades de filtros (rama codex) ----
const createDefaultDateRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end),
  };
};

const formatIsoDateToLong = (value) => {
  if (!value) return '';
  const [y, m, d] = value.split('-').map(n => parseInt(n, 10));
  if ([y, m, d].some(Number.isNaN)) return '';
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

const describeDateRangeSelection = (range) => {
  if (!range) return '';
  const startLabel = formatIsoDateToLong(range.start);
  const endLabel = formatIsoDateToLong(range.end);
  if (startLabel && endLabel) {
    if (range.start === range.end) return startLabel;
    return `${startLabel} a ${endLabel}`;
  }
  if (startLabel) return startLabel;
  if (endLabel) return endLabel;
  return '';
};

const describePeriodSelection = (type, range, month, year) => {
  if (type === 'day') return describeDateRangeSelection(range);
  if (type === 'month') return describeSelectedMonth(month);
  if (type === 'year') return year ? `Año ${year}` : '';
  return '';
};

const EMPTY_DATE_RANGE = { start: '', end: '' };

const PDF_LOGO_CANDIDATES = [
  process.env.REACT_APP_PDF_LOGO,
  '/logobigua.PNG',
  '/DRIZA_LOGO3.png',
  '/DRIZA_LOGO.png',
].filter(Boolean);

const loadImageAsDataUrl = async (src) => {
  const response = await fetch(src, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`No se pudo cargar la imagen: ${src}`);
  }

  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`No se pudo leer la imagen: ${src}`));
    reader.readAsDataURL(blob);
  });
};


const getImageFormatFromDataUrl = (dataUrl = '') => {
  if (typeof dataUrl !== 'string') return 'PNG';
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'PNG';
};

const getPdfLogo = async () => {
  for (const logoSrc of PDF_LOGO_CANDIDATES) {
    try {
      const prefix = process.env.PUBLIC_URL || '';
      const absoluteSrc = logoSrc.startsWith('http') ? logoSrc : `${prefix}${logoSrc}`;
      const dataUrl = await loadImageAsDataUrl(absoluteSrc);
      return { dataUrl, src: absoluteSrc };
    } catch (error) {
      console.warn(`No se pudo cargar el logo de informe en ${logoSrc}:`, error);
    }
  }
  return null;
};

const DashboardComponent = () => {
  const [ingresosMes, setIngresosMes] = useState(0);
  const [clientesActivos, setClientesActivos] = useState(0);
  const [estadoEmbarcaciones, setEstadoEmbarcaciones] = useState({ enElAgua: 0, fueraDelAgua: 0 });
  const [ocupacionResumen, setOcupacionResumen] = useState({ labels: [], data: [], total: 0, promedio: 0, promedioLabel: 'Promedio diario' });
  const [ingresosResumen, setIngresosResumen] = useState({ labels: [], data: [], total: 0, promedio: 0, promedioLabel: 'Promedio mensual' });

  // Estado de filtros: edición (pendiente) y aplicado (efectivo para consultas)
  const [filterType, setFilterType] = useState('day');
  const [dateRange, setDateRange] = useState(createDefaultDateRange);
  const [selectedMonth, setSelectedMonth] = useState(() => formatMonthForInput(new Date()));
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [appliedFilter, setAppliedFilter] = useState(() => ({
    type: 'day',
    dateRange: createDefaultDateRange(),
    month: formatMonthForInput(new Date()),
    year: String(new Date().getFullYear()),
  }));

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 7 }, (_, index) => String(currentYear - index));
  }, []);

  // Derivados del filtro aplicado
  const appliedFilterType = appliedFilter.type;
  const appliedDateRange = appliedFilter.dateRange || EMPTY_DATE_RANGE;
  const appliedMonth = appliedFilter.month;
  const appliedYear = appliedFilter.year;

  // Labels por defecto en base al filtro APLICADO (lo que ve el dashboard)
  const defaultOcupacionLabel = useMemo(() => {
    if (appliedFilterType === 'year') {
      return `Promedio mensual (${appliedYear})`;
    }
    if (appliedFilterType === 'month') {
      const description = describeSelectedMonth(appliedMonth);
      return description ? `Promedio diario (${description})` : 'Promedio diario';
    }
    if (appliedFilterType === 'day') {
      const startDate = parseDateInputValue(appliedDateRange.start);
      const endDate = parseDateInputValue(appliedDateRange.end);
      if (startDate && endDate) {
        const diff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        if (Number.isFinite(diff) && diff > 0) {
          return `Promedio diario (${diff} ${diff === 1 ? 'día' : 'días'})`;
        }
      }
      return 'Promedio diario';
    }
    return 'Promedio diario';
  }, [appliedFilterType, appliedYear, appliedMonth, appliedDateRange.start, appliedDateRange.end]);

  const defaultIngresosLabel = useMemo(() => {
    if (appliedFilterType === 'year') {
      return `Promedio mensual (${appliedYear})`;
    }
    if (appliedFilterType === 'month') {
      const description = describeSelectedMonth(appliedMonth);
      return description ? `Promedio diario (${description})` : 'Promedio diario';
    }
    if (appliedFilterType === 'day') {
      const startDate = parseDateInputValue(appliedDateRange.start);
      const endDate = parseDateInputValue(appliedDateRange.end);
      if (startDate && endDate) {
        const diff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
        if (Number.isFinite(diff) && diff > 0) {
          return `Promedio diario (${diff} ${diff === 1 ? 'día' : 'días'})`;
        }
      }
      return 'Promedio diario';
    }
    return 'Promedio mensual';
  }, [appliedFilterType, appliedYear, appliedMonth, appliedDateRange.start, appliedDateRange.end]);

  // Resumenes (pendiente vs aplicado)
  const pendingFilterSummary = useMemo(() => {
    if (filterType === 'day') {
      const description = describeDateRangeSelection(dateRange);
      return description ? `Rango seleccionado: ${description}` : 'Selecciona un rango de fechas para el filtro diario.';
    }
    if (filterType === 'month') {
      const description = describeSelectedMonth(selectedMonth);
      return description ? `Mes seleccionado: ${description}` : 'Selecciona un mes para analizar.';
    }
    if (filterType === 'year') {
      return selectedYear ? `Año seleccionado: ${selectedYear}` : 'Selecciona un año para analizar.';
    }
    return '';
  }, [filterType, dateRange, selectedMonth, selectedYear]);

  const appliedFilterSummary = useMemo(() => {
    if (!appliedFilterType) return '';
    if (appliedFilterType === 'day') {
      const description = describeDateRangeSelection(appliedDateRange);
      return description ? `Rango aplicado: ${description}` : 'Rango aplicado: personalizado';
    }
    if (appliedFilterType === 'month') {
      const description = describeSelectedMonth(appliedMonth);
      return description ? `Mes aplicado: ${description}` : 'Mes aplicado: personalizado';
    }
    if (appliedFilterType === 'year') {
      return appliedYear ? `Año aplicado: ${appliedYear}` : 'Año aplicado: personalizado';
    }
    return '';
  }, [appliedFilterType, appliedDateRange, appliedMonth, appliedYear]);

  const occupancyChartTitle = useMemo(() => {
    return appliedFilterType === 'year' ? 'Ocupación por mes' : 'Ocupación por día';
  }, [appliedFilterType]);

  const revenueChartTitle = useMemo(() => {
    return appliedFilterType === 'year' ? 'Ingresos por mes' : 'Ingresos por día';
  }, [appliedFilterType]);

  const appliedPeriodDescription = useMemo(
    () => describePeriodSelection(appliedFilterType, appliedDateRange, appliedMonth, appliedYear),
    [appliedFilterType, appliedDateRange, appliedMonth, appliedYear]
  );

  const personasSummaryTitle = useMemo(() => {
    if (appliedFilterType === 'year') return 'Personas en el año';
    if (appliedFilterType === 'month') return 'Personas en el mes';
    return 'Personas en el período';
  }, [appliedFilterType]);

  const personasSummarySubtitle = useMemo(() => {
    if (appliedPeriodDescription) return `Período aplicado: ${appliedPeriodDescription}`;
    return 'Total de personas registradas en el período seleccionado';
  }, [appliedPeriodDescription]);

  const ingresosSummaryTitle = useMemo(() => {
    if (appliedFilterType === 'year') return 'Ingresos del año';
    if (appliedFilterType === 'month') return 'Ingresos del mes';
    return 'Ingresos del período';
  }, [appliedFilterType]);

  const ingresosSummarySubtitle = useMemo(() => {
    if (appliedPeriodDescription) return `Período aplicado: ${appliedPeriodDescription}`;
    return 'Total de ingresos registrados en el período seleccionado';
  }, [appliedPeriodDescription]);

  const totalPersonasPeriodo = ocupacionResumen.total ?? 0;
  const totalIngresosPeriodo = ingresosResumen.total ?? 0;

  const hasPendingChanges = useMemo(() => {
    if (filterType !== appliedFilterType) return true;
    if (filterType === 'day') {
      return (
        dateRange.start !== appliedDateRange.start ||
        dateRange.end !== appliedDateRange.end
      );
    }
    if (filterType === 'month') return selectedMonth !== appliedMonth;
    if (filterType === 'year') return selectedYear !== appliedYear;
    return false;
  }, [
    filterType,
    appliedFilterType,
    dateRange.start,
    dateRange.end,
    appliedDateRange.start,
    appliedDateRange.end,
    selectedMonth,
    appliedMonth,
    selectedYear,
    appliedYear,
  ]);

  const handleApplyFilters = () => {
    setAppliedFilter({
      type: filterType,
      dateRange: { ...dateRange },
      month: selectedMonth,
      year: selectedYear,
    });
  };

  const handleFilterTypeChange = (event) => {
    const value = event.target.value;
    setFilterType(value);
    if (value === 'day') {
      const endDate = new Date();
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 6);
      setDateRange({
        start: formatDateForInput(startDate),
        end: formatDateForInput(endDate),
      });
    } else if (value === 'month') {
      setSelectedMonth(formatMonthForInput(new Date()));
    } else if (value === 'year') {
      setSelectedYear(String(new Date().getFullYear()));
    }
  };

  const handleDateRangeChange = (field) => (event) => {
    const value = event.target.value;
    setDateRange((previous) => {
      const updated = { ...previous, [field]: value };
      if (field === 'start' && value && updated.end && value > updated.end) {
        updated.end = value;
      }
      if (field === 'end' && value && updated.start && value < updated.start) {
        updated.start = value;
      }
      return updated;
    });
  };

  // Params para request usando el FILTRO APLICADO
  const buildFilterParams = useCallback(() => {
    if (appliedFilterType === 'day') {
      return {
        filterType: 'day',
        startDate: appliedDateRange.start || undefined,
        endDate: appliedDateRange.end || undefined,
      };
    }
    if (appliedFilterType === 'month') {
      return {
        filterType: 'month',
        month: appliedMonth || undefined,
      };
    }
    if (appliedFilterType === 'year') {
      return {
        filterType: 'year',
        year: appliedYear || undefined,
      };
    }
    return {};
  }, [appliedFilterType, appliedDateRange.start, appliedDateRange.end, appliedMonth, appliedYear]);

  const handleGenerarInforme = async () => {
    const doc = new jsPDF();
    const logoSize = 20;
    const topMargin = 15;
    const tableStartY = 45;

    const logo = await getPdfLogo();
    if (logo?.dataUrl) {
      doc.addImage(logo.dataUrl, getImageFormatFromDataUrl(logo.dataUrl), 14, topMargin - 8, logoSize, logoSize);
    }

    doc.setFontSize(18);
    doc.text('Informe del Puerto', 105, topMargin, { align: 'center' });

    doc.setFontSize(10);
    const fecha = new Date().toLocaleDateString('es-AR');
    doc.text(`Generado el: ${fecha}`, 105, topMargin + 7, { align: 'center' });

    doc.setFontSize(12);
    doc.text('Datos Principales', 14, topMargin + 15);

    const summaryData = [
      [personasSummaryTitle, `${totalPersonasPeriodo} personas`],
      [ingresosSummaryTitle, formatCurrency(totalIngresosPeriodo)],
      ['Embarcaciones en el Agua', estadoEmbarcaciones.enElAgua],
      ['Embarcaciones Fuera', estadoEmbarcaciones.fueraDelAgua],
      ['Ingresos del Mes', formatCurrency(ingresosMes)],
      ['Clientes Activos', clientesActivos],
      [ocupacionResumen.promedioLabel || defaultOcupacionLabel, `${ocupacionResumen.promedio || 0} personas`],
      [ingresosResumen.promedioLabel || defaultIngresosLabel, formatCurrency(ingresosResumen.promedio || 0)],
    ];

    autoTable(doc, {
      startY: tableStartY,
      head: [['Métrica', 'Valor']],
      body: summaryData,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229] },
    });

    doc.text('Ocupación por período', 14, (doc.lastAutoTable?.finalY || 35) + 10);

    const ocupacionTable = (ocupacionResumen.labels || []).map((label, index) => [
      label,
      ocupacionResumen.data[index] || 0
    ]);

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 35) + 15,
      head: [['Período', 'Ocupación']],
      body: ocupacionTable,
      theme: 'grid',
      styles: { fontSize: 10 },
    });

    doc.text('Ingresos por período', 14, (doc.lastAutoTable?.finalY || 35) + 10);

    const ingresosTable = (ingresosResumen.labels || []).map((label, index) => [
      label,
      formatCurrency(ingresosResumen.data[index] || 0)
    ]);

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 35) + 15,
      head: [['Período', 'Ingresos']],
      body: ingresosTable,
      theme: 'grid',
      styles: { fontSize: 10 },
    });

    doc.save(`informe-puerto-${fecha.replace(/\//g, '-')}.pdf`);

    let csvContent = "Métrica,Valor\n";
    summaryData.forEach(row => {
      csvContent += `${row[0]},${row[1]}\n`;
    });

    csvContent += "\nPeríodo,Ocupación\n";
    ocupacionTable.forEach(row => {
      csvContent += `${row[0]},${row[1]}\n`;
    });

    csvContent += "\nPeríodo,Ingresos\n";
    ingresosTable.forEach(row => {
      csvContent += `${row[0]},${row[1]}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `informe-puerto-${fecha.replace(/\//g, '-')}.csv`);
  };

  const fetchIngresosMes = async () => {
    try {
      const response = await api.get('/ingresos-mes');
      setIngresosMes(response.data.ingresos || 0);
    } catch (error) {
      console.error('Error al obtener los ingresos del mes:', error);
      setIngresosMes(0);
    }
  };

  const fetchEstadoEmbarcaciones = async () => {
    try {
      const response = await api.get('/estado-embarcaciones');
      setEstadoEmbarcaciones(response.data);
    } catch (error) {
      console.error('Error al obtener estado de embarcaciones:', error);
      setEstadoEmbarcaciones({ enElAgua: 0, fueraDelAgua: 0 });
    }
  };

  const fetchClientesActivos = async () => {
    try {
      const response = await api.get('/clientes-activos');
      setClientesActivos(response.data.clientes || 0);
    } catch (error) {
      console.error('Error al obtener los clientes activos:', error);
      setClientesActivos(0);
    }
  };

  const fetchOcupacionData = useCallback(async () => {
    try {
      const response = await api.get('/ocupacion-semanal', { params: buildFilterParams() });
      const resumen = response.data || {};
      setOcupacionResumen({
        labels: resumen.labels || [],
        data: resumen.data || [],
        total: resumen.total ?? 0,
        promedio: resumen.promedio ?? 0,
        promedioLabel: resumen.promedioLabel || defaultOcupacionLabel,
      });
    } catch (error) {
      console.error('Error al obtener datos de ocupación:', error);
      setOcupacionResumen({ labels: [], data: [], total: 0, promedio: 0, promedioLabel: defaultOcupacionLabel });
    }
  }, [buildFilterParams, defaultOcupacionLabel]);

  const fetchIngresosData = useCallback(async () => {
    try {
      const response = await api.get('/ingresos-mensuales', { params: buildFilterParams() });
      const resumen = response.data || {};
      setIngresosResumen({
        labels: resumen.labels || [],
        data: resumen.data || [],
        total: resumen.total ?? 0,
        promedio: resumen.promedio ?? 0,
        promedioLabel: resumen.promedioLabel || defaultIngresosLabel,
      });
    } catch (error) {
      console.error('Error al obtener datos de ingresos mensuales:', error);
      setIngresosResumen({ labels: [], data: [], total: 0, promedio: 0, promedioLabel: defaultIngresosLabel });
    }
  }, [buildFilterParams, defaultIngresosLabel]);

  useEffect(() => {
    const fetchAllData = () => {
      fetchEstadoEmbarcaciones();
      fetchIngresosMes();
      fetchClientesActivos();
      fetchOcupacionData();
      fetchIngresosData();
    };
    fetchAllData();
    const intervalId = setInterval(fetchAllData, 60000);
    return () => clearInterval(intervalId);
  }, [fetchIngresosData, fetchOcupacionData]);

  const occupancyChartData = useMemo(() => ({
    labels: ocupacionResumen.labels,
    datasets: [
      {
        label: 'Ocupación del Puerto',
        data: ocupacionResumen.data,
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgb(79, 70, 229)',
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  }), [ocupacionResumen]);

  const revenueChartData = useMemo(() => ({
    labels: ingresosResumen.labels,
    datasets: [
      {
        label: 'Ingresos',
        data: ingresosResumen.data,
        borderColor: 'rgb(79, 70, 229)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.3,
        fill: true,
        pointBackgroundColor: 'rgb(99, 102, 241)',
        pointBorderColor: '#fff',
        pointHoverRadius: 6,
      },
    ],
  }), [ingresosResumen]);

  return (
    <div className="container mx-auto p-4 space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-3xl font-bold text-indigo-900">Dashboard</h1>
        <DashboardActions onGenerarInforme={handleGenerarInforme} />
      </div>

      <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm space-y-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 text-indigo-700">
            <Filter className="h-4 w-4" />
            <span className="text-sm font-semibold uppercase tracking-wide">Filtros del dashboard</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="filter-type" className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Filtrar por</label>
              <select
                id="filter-type"
                value={filterType}
                onChange={handleFilterTypeChange}
                className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-700 shadow-sm focus:border-indigo-500 focus:outline-none"
              >
                <option value="day">Día</option>
                <option value="month">Mes</option>
                <option value="year">Año</option>
              </select>
            </div>

            {filterType === 'day' && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-indigo-500 uppercase">Desde</span>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={handleDateRangeChange('start')}
                    className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-indigo-500 uppercase">Hasta</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={handleDateRangeChange('end')}
                    className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            )}

            {filterType === 'month' && (
              <div className="flex flex-col">
                <span className="text-xs font-medium text-indigo-500 uppercase">Mes</span>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                />
              </div>
            )}

            {filterType === 'year' && (
              <div className="flex flex-col">
                <span className="text-xs font-medium text-indigo-500 uppercase">Año</span>
                <select
                  value={selectedYear}
                  onChange={(event) => setSelectedYear(event.target.value)}
                  className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-indigo-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="button"
              onClick={handleApplyFilters}
              disabled={!hasPendingChanges}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Confirmar
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1 text-xs">
          {appliedFilterSummary && <span className="text-indigo-500">{appliedFilterSummary}</span>}
          {hasPendingChanges && pendingFilterSummary && (
            <span className="font-medium text-amber-600">Cambios sin aplicar: {pendingFilterSummary}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title={personasSummaryTitle}
          value={`${totalPersonasPeriodo} personas`}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          subtitle={personasSummarySubtitle}
        />

        <SummaryCard
          title={ingresosSummaryTitle}
          value={formatCurrency(totalIngresosPeriodo)}
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
          subtitle={ingresosSummarySubtitle}
        />

        <SummaryCard
          title="Embarcaciones en el Agua"
          value={estadoEmbarcaciones.enElAgua}
          icon={<Ship className="h-4 w-4 text-muted-foreground" />}
          subtitle="Actualizado en tiempo real"
        />

        <SummaryCard
          title="Embarcaciones Fuera"
          value={estadoEmbarcaciones.fueraDelAgua}
          icon={<Anchor className="h-4 w-4 text-muted-foreground" />}
          subtitle="Total de salidas registradas"
        />

        <SummaryCard
          title="Ingresos del Mes"
          value={formatCurrency(ingresosMes)}
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
          subtitle="Total acumulado este mes"
        />

        <SummaryCard
          title="Clientes Activos"
          value={clientesActivos}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          subtitle="Registrados en el sistema"
        />

        <SummaryCard
          title={ocupacionResumen.promedioLabel || defaultOcupacionLabel}
          value={`${ocupacionResumen.promedio} personas`}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
          subtitle="Visitantes promedio en el período"
        />

        <SummaryCard
          title={ingresosResumen.promedioLabel || defaultIngresosLabel}
          value={formatCurrency(ingresosResumen.promedio)}
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
          subtitle="Ingresos promedio del período"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-indigo-900">Ocupación</h2>
              <p className="text-sm text-indigo-600">Visualiza la cantidad de personas que ingresaron al puerto.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs font-medium text-indigo-700">
            <span>Total período: {ocupacionResumen.total} personas</span>
            <span>{ocupacionResumen.promedioLabel || defaultOcupacionLabel}: {ocupacionResumen.promedio} personas</span>
          </div>
          <BarChartCard title={occupancyChartTitle} data={occupancyChartData} />
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-indigo-900">Ingresos</h2>
              <p className="text-sm text-indigo-600">Revisa el comportamiento mensual de los ingresos.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs font-medium text-indigo-700">
            <span>Total período: {formatCurrency(ingresosResumen.total)}</span>
            <span>{ingresosResumen.promedioLabel || defaultIngresosLabel}: {formatCurrency(ingresosResumen.promedio)}</span>
          </div>
          <LineChartCard title={revenueChartTitle} data={revenueChartData} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-white to-indigo-50 border border-indigo-100 rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
          <div className="p-4 border-b border-indigo-100 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white">
            <h2 className="text-sm font-medium text-indigo-700">Próximas Llegadas</h2>
            <Calendar className="h-4 w-4 text-indigo-500" />
          </div>
          <ul className="p-4 space-y-3">
            <li className="flex justify-between items-center py-2 border-b border-indigo-50 last:border-b-0">
              <span className="font-medium">Velero Estrella</span>
              <span className="text-sm text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">Hoy, 14:00</span>
            </li>
            <li className="flex justify-between items-center py-2 border-b border-indigo-50 last:border-b-0">
              <span className="font-medium">Yate Libertad</span>
              <span className="text-sm text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">Mañana, 10:30</span>
            </li>
            <li className="flex justify-between items-center py-2 border-b border-indigo-50 last:border-b-0">
              <span className="font-medium">Crucero Océano</span>
              <span className="text-sm text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">23/06, 08:00</span>
            </li>
          </ul>
        </Card>

        <Card className="bg-gradient-to-br from-white to-indigo-50 border border-indigo-100 rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
          <div className="p-4 border-b border-indigo-100 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white">
            <h2 className="text-sm font-medium text-indigo-700">Alertas Activas</h2>
            <AlertTriangle className="h-4 w-4 text-indigo-500" />
          </div>
          <ul className="p-4 space-y-3">
            <li className="flex items-center py-2 border-b border-indigo-50 last:border-b-0">
              <div className="bg-yellow-100 p-2 rounded-full mr-3">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              </div>
              <div>
                <span className="font-medium text-yellow-700">Mantenimiento programado Muelle A</span>
                <p className="text-xs text-indigo-500 mt-1">Programado para el 25/06</p>
              </div>
            </li>
            <li className="flex items-center py-2 border-b border-indigo-50 last:border-b-0">
              <div className="bg-red-100 p-2 rounded-full mr-3">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <span className="font-medium text-red-700">Alerta de tormenta para mañana</span>
                <p className="text-xs text-indigo-500 mt-1">Posible cancelación de salidas</p>
              </div>
            </li>
          </ul>
        </Card>

        <Card className="bg-gradient-to-br from-white to-indigo-50 border border-indigo-100 rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
          <div className="p-4 border-b border-indigo-100 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white">
            <h2 className="text-sm font-medium text-indigo-700">Servicios Más Solicitados</h2>
            <TrendingUp className="h-4 w-4 text-indigo-500" />
          </div>
          <ul className="p-4 space-y-4">
            <li>
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium">Amarre</span>
                <span className="text-sm font-medium text-indigo-700">65%</span>
              </div>
              <div className="w-full bg-indigo-100 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '65%' }}></div>
              </div>
            </li>
            <li>
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium">Suministro de Combustible</span>
                <span className="text-sm font-medium text-indigo-700">42%</span>
              </div>
              <div className="w-full bg-indigo-100 rounded-full h-2">
                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: '42%' }}></div>
              </div>
            </li>
            <li>
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium">Mantenimiento</span>
                <span className="text-sm font-medium text-indigo-700">28%</span>
              </div>
              <div className="w-full bg-indigo-100 rounded-full h-2">
                <div className="bg-indigo-400 h-2 rounded-full" style={{ width: '28%' }}></div>
              </div>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default DashboardComponent;
